/**
 * Сырые события конвейера (raw_pipeline_events) → строки ленты «История».
 *
 * Правило перевода одно: подпись отвечает на вопрос «что произошло», а не
 * называет имя события. Артикул хранится отдельно от описания — вкладка
 * делает его ссылкой на товар и схлопывает серии вроде «Визуалы готовы» по
 * тринадцати товарам в одну строку.
 */

import type { ActivityEvent } from '../../../../components/ui/ActivityTimeline'
import { pipelineStageInfo } from '../../lib'

export interface RawPipelineEvent {
  id: string | number
  event: string
  article?: string | null
  user_id?: number | null
  /** Имя автора (design-service: base_users.user_name, иначе email). */
  user_name?: string | null
  payload?: any
  created_at: string
}

/** Раздел ленты для фильтров-чипов. «Ошибки» — отдельный срез поверх разделов. */
export type HistoryCategory = 'pipeline' | 'files' | 'visuals' | 'cards'

export interface HistoryEvent extends ActivityEvent {
  /** Имя сырого события: ключ для отладки и тестов. */
  event: string
  article: string | null
  category: HistoryCategory
  /** Событие описывает сбой. */
  isError: boolean
  /** Сырой текст ошибки для разбора на группы («Технический текст» в раскрытии). */
  rawError: string | null
  /** Уточнение перед текстом: шаг конвейера, на котором случилась ошибка. */
  context: string | null
  /** Текст описания без артикула. */
  detail: string | null
  /** Переход стадии «X → X»: ничего не изменилось. */
  stageNoop: boolean
  /** Служебное событие: по умолчанию лента его прячет. */
  technical: boolean
}

/**
 * Подписи, которые интересны только при разборе полёта: служебная перестановка
 * стадий и бэкфилл 04.07. По умолчанию лента их прячет.
 */
export const NOISY_EVENT_TITLES = new Set([
  'Стадия проставлена бэкфиллом',
  'Запрошена генерация визуалов',
  'Запрошены файлы для печати',
  'Повтор шага конвейера',
])

/** Имя автора: «Мария Орлова»; без имени — «пользователь #25»; без автора — «система». */
export const eventActor = (e: Pick<RawPipelineEvent, 'user_id' | 'user_name'>): string => {
  const name = String(e.user_name ?? '').trim()
  if (name) return name
  return e.user_id ? `пользователь #${e.user_id}` : 'система'
}

const join = (...parts: Array<string | null | undefined | false>): string | null =>
  parts.filter(Boolean).join(' · ') || null

export function mapPipelineEvent(e: RawPipelineEvent): HistoryEvent {
  const p = e.payload || {}
  const article = e.article ? String(e.article) : null
  const stageLabel = (s?: string | null) => (s ? pipelineStageInfo(s)?.label || s : '—')

  const make = (
    over: Pick<HistoryEvent, 'type' | 'title'> &
      Partial<Pick<HistoryEvent, 'category' | 'isError' | 'rawError' | 'detail' | 'stageNoop' | 'diff' | 'context'>>,
  ): HistoryEvent => {
    const detail = over.detail ?? null
    const stageNoop = over.stageNoop ?? false
    return {
      id: String(e.id),
      at: e.created_at,
      actor: eventActor(e),
      type: over.type,
      title: over.title,
      diff: over.diff,
      event: e.event,
      article,
      category: over.category ?? 'pipeline',
      isError: over.isError ?? false,
      rawError: over.rawError ?? null,
      context: over.context ?? null,
      detail,
      stageNoop,
      technical: NOISY_EVENT_TITLES.has(over.title) || stageNoop,
      // Совместимость с ActivityTimeline: описание целиком, с артикулом.
      description: join(article, detail) ?? undefined,
    }
  }

  switch (e.event) {
    case 'pipeline.status.changed': {
      const before = stageLabel(p.previous_status)
      const after = stageLabel(p.pipeline_status)
      const error = p.pipeline_error ? String(p.pipeline_error) : null
      const same = before === after
      // Тот же статус без текста ошибки — пустой переход. С новым текстом ошибки
      // это повторный сбой, и его причина важна.
      if (same && error) {
        return make({
          type: 'sync',
          title: 'Повторная ошибка конвейера',
          isError: true,
          rawError: error,
          detail: error,
        })
      }
      return make({
        type: 'sync',
        title: 'Стадия конвейера',
        diff: same ? undefined : { before, after },
        stageNoop: same,
        isError: p.pipeline_status === 'error',
        rawError: error,
        detail: error,
      })
    }
    case 'design.accepted':
      return make({ type: 'moderation', title: 'Макет принят' })
    case 'design.returned':
      return make({ type: 'moderation', title: 'Возвращён дизайнеру', detail: p.comment || null })
    case 'design.approved':
      return make({ type: 'moderation', title: 'Публикация одобрена' })
    case 'pipeline.status.backfilled':
      return make({
        type: 'sync',
        title: 'Стадия проставлена бэкфиллом',
        detail: stageLabel(p.pipeline_status),
      })
    case 'pipeline.error': {
      const error = p.error ? String(p.error) : null
      return make({
        type: 'other',
        title: 'Ошибка конвейера',
        isError: true,
        rawError: error,
        context: p.step ? `шаг «${String(p.step)}»` : null,
        detail: error ?? (p.step ? String(p.step) : null),
      })
    }
    case 'media.generation.requested':
      return make({
        type: 'media',
        category: 'visuals',
        title: 'Запрошена генерация визуалов',
        detail: p.cabinet ? `кабинет ${String(p.cabinet).toUpperCase()}` : null,
      })
    case 'media.generation.completed':
      return make({ type: 'media', category: 'visuals', title: 'Визуалы готовы' })
    case 'media.generation.failed':
      return make({
        type: 'media',
        category: 'visuals',
        title: 'Генерация визуалов не удалась',
        isError: true,
        rawError: p.error ? String(p.error) : null,
        detail: p.error ? String(p.error) : null,
      })
    case 'pipeline.tech_check.completed':
      return make({
        type: 'moderation',
        title: 'Файлы проверены',
        detail: p.passed === false ? 'Есть замечания' : null,
      })
    case 'pipeline.retry.requested':
      return make({ type: 'sync', title: 'Повтор шага конвейера' })
    case 'products.created':
      return make({
        type: 'stock',
        title: 'Товары 1С созданы',
        detail: p.count != null ? `Товаров: ${p.count}` : null,
      })
    case 'card.created':
      return make({ type: 'stock', category: 'cards', title: 'Карточка маркетплейса создана' })
    case 'card.failed':
      return make({
        type: 'stock',
        category: 'cards',
        title: 'Ошибка карточки маркетплейса',
        isError: true,
        rawError: p.error ? String(p.error) : null,
        detail: p.error ? String(p.error) : null,
      })
    case 'production.files.requested':
      return make({ type: 'description', category: 'files', title: 'Запрошены файлы для печати' })
    case 'production.files.completed':
      return make({ type: 'description', category: 'files', title: 'Файлы для печати готовы' })
    case 'production.files.failed':
      return make({
        type: 'description',
        category: 'files',
        title: 'Файлы для печати не созданы',
        isError: true,
        rawError: p.error ? String(p.error) : null,
        detail: p.error ? String(p.error) : null,
      })
    default:
      if (e.event.startsWith('product')) {
        return make({ type: 'stock', category: 'cards', title: 'Товары', detail: e.event })
      }
      if (e.event.startsWith('card')) {
        return make({ type: 'stock', category: 'cards', title: 'Карточка маркетплейса', detail: e.event })
      }
      return make({ type: 'other', title: e.event })
  }
}
